import { action, computed, makeAutoObservable, observable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import {
  IS_DEV,
} from "../resources/constants";
import {
  getPageTitle,
  NSW_HOME_KIOSK_ROUTES,
  NSW_HOME_ROUTES,
  NSW_HOUSE_LAND_KIOSK_ROUTES,
  NSW_HOUSE_LAND_ROUTES,
} from "../resources/routes";
import {
  Consultant,
  ExteriorStyle,
  Facade,
  FacadeFinishOption,
  FloorplanOption,
  Home,
  HouseLand,
  Inclusion,
  InteriorStyle,
  LandType,
  Promotion,
  RoutePath,
  SessionState,
  Upgrade,
  UpgradeCategory,
  UpgradeStyle,
} from "../types";
import Rendivo from "../util/rendivo";
import { ApiStore } from "./ApiStore";

/**
 * User Store contains app state relating to the User's selections throughout the journey, and their account details
 * This state will be specific to the user's current journey through the app, and will be syncronised with their account
 * in the database
 */
export class UserStore {
  // We need the Api Store to access saved user data
  @observable apiStore: ApiStore;

  @observable isLoggedIn = false;
  @observable isNewUser = false;

  // Did the user enter the by restoring their session?
  @observable enteredViaRestoreSession = false;
  @observable isMyDomaineMode = false;
  @observable isShareMode = false;
  @observable isKioskMode = false;
  @observable showKioskSplash = true;
  @observable hasKioskMenu = false;

  // Unique ID for this user's session. Also used to restore a previous session.
  @observable uuid: string | null = null;

  @observable previousRoute: RoutePath = RoutePath.HOME;
  @observable currentRoute: RoutePath = RoutePath.HOME;

  // User details found in Signup form
  // Will need to be extracted from CMS User Account once that is implemented
  @observable firstName = "";
  @observable lastName = "";
  @observable phone = "";
  @observable email = "";
  @observable emailSecondary = "";
  @observable password = "";
  @observable optIn = false;
  @observable sendToFinanceManager = false;
  @observable interestedInGrannyFlats = false;
  @observable consent = true;

  @observable landType: LandType | null = null;

  @observable selectedHome: Home | null = null;

  @observable selectedHouseLand: HouseLand | null = null;

  // Keyed by Home ID
  @observable isFloorplanFlipped: Map<number, boolean> = new Map<
    number,
    boolean
  >();

  // We store selected options for ALL homes, so that when the user navigates backwards we can
  // remember and show them all previous selections
  // Map key = homeId
  @observable selectedFloorplanOptions: Map<number, FloorplanOption[]> =
    new Map<number, FloorplanOption[]>();

  @observable selectedFacade: Facade | null = null;
  // We store selected options for ALL facades, so that when the user navigates backwards we can
  // remember and show them all previous selections
  // Map key = facadeId
  @observable selectedFacadeFinishOption: Map<number, FacadeFinishOption> =
    new Map<number, FacadeFinishOption>();

  @observable selectedExteriorStyle: ExteriorStyle | null = null;
  // Map key = exteriorStyleId
  @observable selectedRoofStyle: Map<number, UpgradeStyle> = new Map<
    number,
    UpgradeStyle
  >();

  @observable selectedWindowStyle: Map<number, UpgradeStyle> = new Map<
    number,
    UpgradeStyle
  >();

  @observable selectedGutterStyle: Map<number, UpgradeStyle> = new Map<
    number,
    UpgradeStyle
  >();

  @observable selectedInclusion: Inclusion | null = null;

  // Keyed by Upgrade Category Id
  @observable selectedUpgrades: Map<number, Upgrade> = new Map<
    number,
    Upgrade
  >();

  @observable selectedTurnKeyInclusion: Inclusion | null = null;

  @observable selectedInteriorStyle: InteriorStyle | null = null;

  @observable selectedKitchenStyle: Map<number, UpgradeStyle> = new Map<
    number,
    UpgradeStyle
  >();
  // Map key = interiorStyleId
  @observable selectedFlooringStyle: Map<number, UpgradeStyle> = new Map<
    number,
    UpgradeStyle
  >();

  @observable selectedLifestyleOptions: Map<number, Upgrade[]> = new Map<
    number,
    Upgrade[]
  >();

  @observable selectedPromotion: Promotion | null = null;
  @observable selectedSpecialOffer: Promotion | null = null;

  // Financial Situation Form
  @observable applyForGrant: boolean | null = null;
  @observable financialSituation: string | null = null;
  @observable buildTimeline: string | null = null;
  @observable leadStatus: string | null = null;
  @observable landSituation: string | null = null;
  @observable landRegistrationDate = "";
  @observable finalComments = "";
  @observable consultant: Consultant | null = null;

  //nsw fields
  @observable rememberDetails = true;

  constructor(apiStore: ApiStore) {
    this.apiStore = apiStore;
    makeAutoObservable(this);
  }

  @action reset() {
    this.enteredViaRestoreSession = false;
    this.isNewUser = false;
    this.landType = null;
    this.selectedHome = null;
    this.selectedHouseLand = null;
    this.isFloorplanFlipped.clear();
    this.selectedFloorplanOptions.clear();
    this.selectedFacade = null;
    this.selectedFacadeFinishOption.clear();
    this.selectedExteriorStyle = null;
    this.selectedRoofStyle.clear();
    this.selectedWindowStyle.clear();
    this.selectedGutterStyle.clear();
    this.selectedInclusion = null;
    this.selectedUpgrades.clear();
    this.selectedTurnKeyInclusion = null;
    this.selectedInteriorStyle = null;
    this.selectedKitchenStyle.clear();
    this.selectedFlooringStyle.clear();
    this.selectedLifestyleOptions.clear();
    this.selectedSpecialOffer = null;
    this.applyForGrant = null;
    this.financialSituation = null;
    this.buildTimeline = null;
    this.finalComments = "";
    this.uuid = uuidv4();
    this.showKioskSplash = true;
  }

  @computed get basePrice(): number {
    if (this.selectedHome) {
      return this.selectedHome.price;
    } else if (this.selectedHouseLand) {
      return this.selectedHouseLand.price;
    }
    return 0;
  }

  @computed get floorplanPrice() {
    let targetId = -1;
    if (this.selectedHome) {
      targetId = this.selectedHome.id;
    } else if (this.selectedHouseLand) {
      targetId = this.selectedHouseLand.id;
    }

    let price = 0;
    if (targetId !== -1) {
      const floorplanOptions = this.selectedFloorplanOptions.get(targetId);
      if (floorplanOptions) {
        for (const option of floorplanOptions) {
          price += option.price;
        }
      }
    }
    return price;
  }

  @computed get facadePrice() {
    let price = 0;
    if (this.selectedFacade) {
      price += this.selectedFacade.base_price;
      const selectedFinish = this.selectedFacadeFinishOption.get(
        this.selectedFacade.id
      );
      if (selectedFinish && !selectedFinish.included) {
        price += selectedFinish.price - this.selectedFacade.base_price;
      }
    }
    return price;
  }

  @computed get exteriorStylePrice() {
    let price = 0;
    if (this.selectedExteriorStyle) {
      price += this.selectedExteriorStyle.price;
      const selectedRoofStyle = this.selectedRoofStyle.get(
        this.selectedExteriorStyle.id
      );
      if (selectedRoofStyle) {
        if (
          this.selectedHome &&
          this.selectedHome.roof_upgrades[selectedRoofStyle.id] !== undefined
        ) {
          price += this.selectedHome.roof_upgrades[selectedRoofStyle.id];
        } else if (
          this.selectedHouseLand &&
          this.selectedHouseLand.roof_upgrades[selectedRoofStyle.id] !==
            undefined
        ) {
          price += this.selectedHouseLand.roof_upgrades[selectedRoofStyle.id];
        } else {
          price += selectedRoofStyle.price;
        }
      }

      const selectedGutterStyle = this.selectedGutterStyle.get(
        this.selectedExteriorStyle.id
      );
      if (selectedGutterStyle) {
        price += selectedGutterStyle.price;
      }

      const selectedWindowStyle = this.selectedWindowStyle.get(
        this.selectedExteriorStyle.id
      );
      if (selectedWindowStyle) {
        price += selectedWindowStyle.price;
      }
    }
    return price;
  }

  @computed get inclusionsPrice(): number {
    if (this.selectedInclusion) {
      return this.getPriceOrPromoPrice(this.selectedInclusion);
    }
    return 0;
  }

  @computed get inclusionsPriceWithPromo(): number {
    if(this.selectedPromotion && ["dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(this.selectedPromotion.type)){
      let price = 0;
      if (this.selectedInclusion) {
        if (Object.keys(this.selectedPromotion.inclusions).indexOf(this.selectedInclusion.id.toString()) > -1) {
          price = price + this.selectedInclusion.price;
        }
      }
      return price;
    }else{
      return 0;
    }
  }

  @computed get upgradesPrice(): number {
    let price = 0;
    for (const item of Array.from(this.selectedUpgrades)) {
      price += this.getPriceOrPromoPrice(item[1]);
    }
    return price;
  }

  @computed get upgradesPriceWithPromo(): number {
    if(this.selectedPromotion && ["dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(this.selectedPromotion.type)){
      let price = 0;
      for (const item of Array.from(this.selectedUpgrades)) {
        const upgrade = item[1];
        if (Object.keys(this.selectedPromotion.upgrades).indexOf(upgrade.id.toString()) > -1) {
          price = price + upgrade.price;
        }
      }
      return price;
    }else{
      return 0;
    }
  }

  @computed get turnKeyPrice(): number {
    if (this.selectedTurnKeyInclusion) {
      return this.getPriceOrPromoPrice(this.selectedTurnKeyInclusion);
    }
    return 0;
  }

  @computed get interiorStylePrice(): number {
    let price = 0;
    if (this.selectedInteriorStyle) {
      price += this.selectedInteriorStyle.price;

      const selectedKitchenStyle = this.selectedKitchenStyle.get(
        this.selectedInteriorStyle.id
      );
      if (selectedKitchenStyle) {
        if (
          this.selectedHome &&
          this.selectedHome.kitchen_upgrades[selectedKitchenStyle.id] !==
            undefined
        ) {
          price += this.selectedHome.kitchen_upgrades[selectedKitchenStyle.id];
        } else if (
          this.selectedHouseLand &&
          this.selectedHouseLand.kitchen_upgrades[selectedKitchenStyle.id] !==
            undefined
        ) {
          price +=
            this.selectedHouseLand.kitchen_upgrades[selectedKitchenStyle.id];
        } else {
          price += selectedKitchenStyle.price;
        }
      }

      const selectedFlooringStyle = this.selectedFlooringStyle.get(
        this.selectedInteriorStyle.id
      );
      if (selectedFlooringStyle) {
        if (
          this.selectedHome &&
          this.selectedHome.flooring_upgrades[selectedFlooringStyle.id] !==
            undefined
        ) {
          price +=
            this.selectedHome.flooring_upgrades[selectedFlooringStyle.id];
        } else if (
          this.selectedHouseLand &&
          this.selectedHouseLand.flooring_upgrades[selectedFlooringStyle.id] !==
            undefined
        ) {
          price +=
            this.selectedHouseLand.flooring_upgrades[selectedFlooringStyle.id];
        } else {
          price += selectedFlooringStyle.price;
        }
      }
    }
    return price;
  }

  @computed get lifestyleOptionsPrice(): number {
    let price = 0;
    for (const item of Array.from(this.selectedLifestyleOptions)) {
      for (const upgrade of item[1]) {
        price += this.getPriceOrPromoPrice(upgrade);
      }
    }
    return price;
  }

  @computed get lifestyleOptionsPriceWithPromo(): number {
    if(this.selectedPromotion && ["dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(this.selectedPromotion.type)){
      let price = 0;
      for (const item of Array.from(this.selectedLifestyleOptions)) {
        for (const upgrade of item[1]) {
          if (Object.keys(this.selectedPromotion.lifestyle_options).indexOf(upgrade.id.toString()) > -1) {
            price = price + upgrade.price;
          }
        }
      }
      return price;
    }else{
      return 0;
    }
  }

  @computed get siteWorksAmount(): number {
    if (
      this.landType !== LandType.HOUSE_LAND_PACKAGE &&
      this.apiStore.estate &&
      this.apiStore.estate.site_cost !== null
    ) {
      return this.apiStore.estate.site_cost;
    }
    return 0;
  }

  @computed get landCostAmount(): number {
    if (
      this.landType === LandType.HOUSE_LAND_PACKAGE &&
      this.selectedHouseLand !== null
    ) {
      return this.selectedHouseLand.land_cost;
    }
    return 0;
  }

  @computed get appliedGrantAmount(): number {
    if (this.applyForGrant === true) {
      return this.apiStore.grantSettings.grant_amount;
    }
    return 0;
  }

  @computed get subtotalPrice(): number {
    let price = 0;
    price += this.basePrice;
    price += this.floorplanPrice;
    price += this.facadePrice;
    price += this.exteriorStylePrice;
    price += this.inclusionsPrice;
    price += this.upgradesPrice;
    price += this.turnKeyPrice;
    price += this.interiorStylePrice;
    price += this.lifestyleOptionsPrice;
    return price;
  }

  @computed get subtotalPriceWithSiteWorks(): number {
    return this.subtotalPrice + this.siteWorksAmount;
  }

  @computed get subtotalPriceWithoutLandCost(): number {
    return this.subtotalPrice - this.landCostAmount;
  }

  @computed get totalPrice(): number {
    return (
      this.subtotalPrice - this.specialOfferAmount - this.appliedGrantAmount
    );
  }

  @computed get totalPriceWithSiteWorks(): number {
    return (
      this.subtotalPriceWithSiteWorks -
      this.specialOfferAmount -
      this.appliedGrantAmount
    );
  }

  /**
   * 'Promotions' affect Lifestyle, Upgrade, Inclusion options
   */
  @computed get promotions(): Promotion[] {
    if (this.selectedHome && this.selectedHome.promotions) {
      return this.selectedHome.promotions.filter((item) => {
        return (
          [
            "no_additional_cost",
            "price_reduction",
            "dollar_off_home_plus_no_additional_cost",
            "dollar_amount_bonus",
            "dollar_amount_bonus_plus_floorplan_options",
          ].indexOf(item.type) > -1
        );
      });
    }
    return [];
  }

  getInclusionPromoPricing(
    key: "inclusions" | "upgrades" | "lifestyle_options" | "turn_key_inclusions"
  ): Inclusion[] | Upgrade[] {
    let homeOrHouseLand = null;
    if (this.selectedHome) {
      homeOrHouseLand = { ...this.selectedHome };
    } else if (this.selectedHouseLand) {
      homeOrHouseLand = { ...this.selectedHouseLand };
    }

    if (homeOrHouseLand) {
      if (!this.selectedPromotion || 
        ["dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(this.selectedPromotion.type) //pricing is handled in specialOfferAmount
      ){
        return homeOrHouseLand[key];
      }
      
      const items: Inclusion[] = [];
      for (const item of homeOrHouseLand[key]) {
        let promo_price;
        if (
          Object.keys(this.selectedPromotion[key]).indexOf(item.id.toString()) >
          -1
        ) {
          promo_price = this.selectedPromotion[key][item.id];
        }

        items.push({
          ...item,
          promo_price,
        });
      }
      return items;
    }

    return [];
  }

  @computed get inclusionsWithPromoPricing(): Inclusion[] {
    return this.getInclusionPromoPricing("inclusions");
  }

  @computed get upgradesWithPromoPricing(): Upgrade[] {
    return this.getInclusionPromoPricing("upgrades") as Upgrade[];
  }

  @computed get lifestyleOptionsWithPromoPricing(): Upgrade[] {
    return this.getInclusionPromoPricing("lifestyle_options") as Upgrade[];
  }

  @computed get turnKeyInclusionsWithPromoPricing(): Inclusion[] {
    return this.getInclusionPromoPricing("turn_key_inclusions");
  }

  @computed get floorplanOptionsAmount(): number {
    if(this.selectedHome){
      const selectedFloorplanOptions = this.selectedFloorplanOptions.get(this.selectedHome.id) || [];
      return selectedFloorplanOptions.reduce((a,v) =>  a = a + v.price , 0);
    }else{
      return 0;
    }
  }

  getCategories(
    key: "upgrade_category" | "lifestyle_option_category"
  ): UpgradeCategory[] {
    let homeOrHouseLand = null;
    if (this.selectedHome) {
      homeOrHouseLand = { ...this.selectedHome };
    } else if (this.selectedHouseLand) {
      homeOrHouseLand = { ...this.selectedHouseLand };
    }

    if (homeOrHouseLand && homeOrHouseLand[key] !== undefined) {
      return homeOrHouseLand[key];
    }
    return [];
  }

  @computed get upgradeCategories(): UpgradeCategory[] {
    return this.getCategories("upgrade_category");
  }

  @computed get lifestyleCategories(): UpgradeCategory[] {
    return this.getCategories("lifestyle_option_category");
  }

  /**
   * 'Special Offers' are promos that affect the overall price of a home
   */
  @computed get specialOffers(): Promotion[] {
    if (this.selectedHome && this.selectedHome.promotions) {
      return this.selectedHome.promotions.filter((item) => {
        return (
          [
            "perc_off_home",
            "dollar_off_home",
            "dollar_off_home_via_pivot",
          ].indexOf(item.type) > -1
        );
      });
    }
    return [];
  }

  @computed get specialOfferAmount(): number {
    if (this.selectedSpecialOffer) {
      if (this.selectedSpecialOffer.type === "perc_off_home") {
        if (this.selectedHome) {
          return (
            this.subtotalPrice * (this.selectedSpecialOffer.amount / 100)
          );
        } else if (this.selectedHouseLand) {
          return (
            this.subtotalPriceWithSiteWorks * (this.selectedSpecialOffer.amount / 100)
          );
        } else {
          return 0;
        }
      } else {
        return this.selectedSpecialOffer.amount;
      }
    }else if(this.selectedPromotion && this.selectedPromotion.type === "dollar_off_home_plus_no_additional_cost"){
      return this.selectedPromotion.amount;
    }else if(this.selectedPromotion && ["dollar_amount_bonus","dollar_amount_bonus_plus_floorplan_options"].includes(this.selectedPromotion.type)){
      let additionalSelectionsAmount = this.lifestyleOptionsPriceWithPromo + this.inclusionsPriceWithPromo + this.upgradesPriceWithPromo;
      if(this.selectedPromotion.type === "dollar_amount_bonus_plus_floorplan_options"){
        additionalSelectionsAmount = additionalSelectionsAmount + this.floorplanOptionsAmount;
      }
      if(additionalSelectionsAmount > this.selectedPromotion.amount){
        return this.selectedPromotion.amount;
      }else{
        return additionalSelectionsAmount;
      }
    }

    return 0;
  }

  @computed get isCladdedSelected() {
    if (this.selectedFacade) {
      const selectedFinish = this.selectedFacadeFinishOption.get(
        this.selectedFacade.id
      );
      if (selectedFinish) {
        if (selectedFinish.label.toLowerCase().indexOf("cladded") > -1) {
          return true;
        }
      }
    }
    return false;
  }

  @computed get rendivoExteriorRender() {
    let rendivoProductId = "";
    let rendivoFinishId = "";
    let rendivoColourId = "";
    let rendivoRoofId = "";
    let rendivoGutterId = "";
    let rendivoWindowId = "";
    let isFlipped = false;
    const style = this.isCladdedSelected ? "CLADDED" : null;
    if (
      this.landType !== LandType.HOUSE_LAND_PACKAGE &&
      this.selectedHome &&
      this.selectedFacade
    ) {
      isFlipped = this.isFloorplanFlipped.get(this.selectedHome.id) || false;
      rendivoProductId = this.selectedFacade.rendivo_id;
      const selectedFinish = this.selectedFacadeFinishOption.get(
        this.selectedFacade.id
      );
      if (selectedFinish) {
        rendivoFinishId = selectedFinish.rendivo_id;
      }
    } else if (
      this.landType === LandType.HOUSE_LAND_PACKAGE &&
      this.selectedHouseLand !== null
    ) {
      rendivoProductId = this.selectedHouseLand.rendivo_id;
      rendivoFinishId = this.selectedHouseLand.rendivo_theme_id;
      isFlipped =
        this.isFloorplanFlipped.get(this.selectedHouseLand.id) || false;
    }

    const selectedExterior = this.selectedExteriorStyle;
    if (selectedExterior) {
      rendivoColourId = selectedExterior.rendivo_id;
      const selectedRoof = this.selectedRoofStyle.get(selectedExterior.id);
      if (selectedRoof) {
        rendivoRoofId = selectedRoof.rendivo_id;
      }

      const selectedGutter = this.selectedGutterStyle.get(selectedExterior.id);
      if (selectedGutter) {
        rendivoGutterId = selectedGutter.rendivo_id;
      }

      const selectedWindow = this.selectedWindowStyle.get(selectedExterior.id);
      if (selectedWindow) {
        rendivoWindowId = selectedWindow.rendivo_id;
      }
    }

    return Rendivo.getExteriorRender(
      1640,
      rendivoProductId,
      style,
      rendivoFinishId,
      rendivoColourId,
      rendivoRoofId,
      rendivoGutterId,
      rendivoWindowId,
      isFlipped
    );
  }

  @computed get currentStep(): number {
    const routes = this.getRouteList();
    let currentStep = 1;
    let currentRouteIndex = routes.indexOf(this.currentRoute);
    if (currentRouteIndex === -1) {
      // Route not found - might be login route
      currentRouteIndex = routes.indexOf(this.previousRoute);
    }
    if (currentRouteIndex > -1) {
      currentStep = currentRouteIndex + 1;
    }
    return currentStep;
  }

  @computed get percentComplete(): number {
    const routes = this.getRouteList();
    return Math.ceil((this.currentStep / routes.length) * 100);
  }

  @computed get canSaveAppState() {
    const routes = this.getRouteList();
    const currentRouteIndex = routes.indexOf(this.currentRoute);
    const minimumRouteIndex = routes.indexOf(RoutePath.CHOOSE_EXTERIOR_STYLE);
    return (
      this.uuid && currentRouteIndex >= minimumRouteIndex
    );
  }

  @computed get isShareOrMyDomaineMode() {
    return this.isShareMode || this.isMyDomaineMode;
  }

  @action
  setIsMyDomaineMode(value: boolean) {
    this.isMyDomaineMode = value;
  }

  @action
  setIsShareMode(value: boolean) {
    this.isShareMode = value;
  }

  @action
  setUUID(uuid: string) {
    this.uuid = uuid;
  }

  @action
  setPreviousRoute(route: RoutePath) {
    this.previousRoute = route;
  }

  @action
  setCurrentRoute(route: RoutePath) {
    this.currentRoute = route;
  }

  @action
  setIsLoggedIn(isLoggedIn: boolean) {
    this.isLoggedIn = isLoggedIn;
  }

  @action
  setIsNewUser(isNewUser: boolean) {
    this.isNewUser = isNewUser;
  }

  @action
  setIsKioskMode(isKioskMode: boolean) {
    this.isKioskMode = isKioskMode;
  }

  @action
  setShowKioskSplash(showKioskSplash: boolean) {
    this.showKioskSplash = showKioskSplash;
  }

  @action
  setHasKioskMenu(hasKioskMenu: boolean) {
    this.hasKioskMenu = hasKioskMenu;
  }

  @action setFirstName(firstName: string) {
    this.firstName = firstName;
  }

  @action setLastName(lastName: string) {
    this.lastName = lastName;
  }

  @action setPhone(phone: string) {
    this.phone = phone;
  }

  @action setEmail(email: string) {
    this.email = email;
  }

  @action setEmailSecondary(emailSecondary: string) {
    this.emailSecondary = emailSecondary;
  }

  @action setSendToFinanceManager(sendToFinanceManager: boolean) {
    this.sendToFinanceManager = sendToFinanceManager;
  }

  @action setInterestedInGrannyFlats(interestedInGrannyFlats: boolean) {
    this.interestedInGrannyFlats = interestedInGrannyFlats;
  }

  @action setConsent(consent: boolean) {
    this.consent = consent;
  }

  @action setPassword(password: string) {
    this.password = password;
  }

  @action setOptIn(optIn: boolean) {
    this.optIn = optIn;
  }

  @action resetSignUpFields() {
    this.firstName = "";
    this.lastName = "";
    this.phone = "";
    this.email = "";
    this.emailSecondary = "";
    this.password = "";
    this.optIn = false;
  }

  @action
  setLandType(landType: LandType) {
    this.landType = landType;
  }

  @action
  setSelectedHome(home: Home | null) {
    this.selectedHome = home;
  }

  @action
  setSelectedHouseLand(houseLand: HouseLand | null) {
    this.selectedHouseLand = houseLand;
  }

  @action
  resetIsFloorplanFlipped() {
    this.isFloorplanFlipped.clear();
  }

  @action
  setIsFloorplanFlipped(homeId: number, isFlipped: boolean) {
    this.isFloorplanFlipped.set(homeId, isFlipped);
  }

  @action
  resetSelectedFloorplanOptions() {
    this.selectedFloorplanOptions.clear();
  }

  @action
  setSelectedFloorplanOptions(homeId: number, options: FloorplanOption[]) {
    this.selectedFloorplanOptions.set(homeId, options);
  }

  @action
  toggleSelectedFloorplanOption(homeId: number, option: FloorplanOption) {
    const options = this.selectedFloorplanOptions.get(homeId);
    if (options) {
      const optionIds = options.map((item) => item.id);
      const optionIndex = optionIds.indexOf(option.id);
      if (optionIndex === -1) {
        options.push(option);
      } else {
        options.splice(optionIndex, 1);
      }

      for (const conflictId of option.conflicts) {
        // Unselect any conflicts
        const optionIndex = optionIds.indexOf(conflictId);
        if (optionIndex > -1) {
          options.splice(optionIndex, 1);
        }
      }

      this.setSelectedFloorplanOptions(homeId, options);
    } else {
      this.setSelectedFloorplanOptions(homeId, [option]);
    }
  }

  @action
  setSelectedFacade(facade: Facade | null) {
    this.selectedFacade = facade;
  }

  @action
  resetSelectedFacadeFinishOption() {
    this.selectedFacadeFinishOption.clear();
  }

  @action
  setSelectedFacadeFinishOption(facadeId: number, option: FacadeFinishOption) {
    this.selectedFacadeFinishOption.set(facadeId, option);
  }

  @action
  setSelectedExteriorStyle(exteriorStyle: ExteriorStyle | null) {
    this.selectedExteriorStyle = exteriorStyle;
  }

  @action
  resetSelectedRoofStyle() {
    this.selectedRoofStyle.clear();
  }

  @action
  setSelectedRoofStyle(exteriorId: number, option: UpgradeStyle) {
    this.selectedRoofStyle.set(exteriorId, option);
  }

  @action
  resetSelectedSelectedGutterStyle() {
    this.selectedGutterStyle.clear();
  }

  @action
  setSelectedGutterStyle(exteriorId: number, option: UpgradeStyle) {
    this.selectedGutterStyle.set(exteriorId, option);
  }

  @action
  resetSelectedSelectedWindowStyle() {
    this.selectedWindowStyle.clear();
  }

  @action
  setSelectedWindowStyle(exteriorId: number, option: UpgradeStyle) {
    this.selectedWindowStyle.set(exteriorId, option);
  }

  @action
  setSelectedInclusion(inclusion: Inclusion | null) {
    this.selectedInclusion = inclusion;
  }

  @action
  setSelectedUpgrade(categoryId: number, upgrade: Upgrade | null) {
    if (upgrade == null) {
      this.selectedUpgrades.delete(categoryId);
    } else {
      this.selectedUpgrades.set(categoryId, upgrade);
    }
  }

  @action
  resetSelectedUpgrades() {
    this.selectedUpgrades.clear();
  }

  @action
  setSelectedTurnKeyInclusion(inclusion: Inclusion | null) {
    this.selectedTurnKeyInclusion = inclusion;
  }

  @action
  setSelectedInteriorStyle(interiorStyle: InteriorStyle | null) {
    this.selectedInteriorStyle = interiorStyle;
  }

  @action
  resetSelectedKitchenStyle() {
    this.selectedKitchenStyle.clear();
  }

  @action
  setSelectedKitchenStyle(interiorId: number, option: UpgradeStyle) {
    this.selectedKitchenStyle.set(interiorId, option);
  }

  @action
  resetSelectedFlooringStyle() {
    this.selectedFlooringStyle.clear();
  }

  @action
  setSelectedFlooringStyle(interiorId: number, option: UpgradeStyle) {
    this.selectedFlooringStyle.set(interiorId, option);
  }

  @action
  setSelectedLifestyleOption(categoryId: number, options: Upgrade[]) {
    this.selectedLifestyleOptions.set(categoryId, options);
  }

  @action
  toggleSelectedLifestyleOption(categoryId: number, upgrade: Upgrade) {
    let lifestyleOptions = this.selectedLifestyleOptions.get(categoryId);
    if (!lifestyleOptions) {
      lifestyleOptions = [upgrade];
    } else {
      let index = -1;
      for (let i = 0; i < lifestyleOptions.length; i++) {
        if (lifestyleOptions[i].id === upgrade.id) {
          index = i;
          break;
        }
      }
      if (index === -1) {
        lifestyleOptions.push(upgrade);
      } else {
        lifestyleOptions.splice(index, 1);
      }
    }
    this.selectedLifestyleOptions.set(categoryId, lifestyleOptions);
  }

  @action
  resetSelectedLifestyleOptions() {
    this.selectedLifestyleOptions.clear();
  }

  @action
  setSelectedPromotion(promotion: Promotion | null) {
    this.selectedPromotion = promotion;
  }

  @action
  setSelectedSpecialOffer(offer: Promotion | null) {
    this.selectedSpecialOffer = offer;
  }

  @action
  setApplyForGrant(val: boolean | null) {
    this.applyForGrant = val;
  }

  @action
  setFinancialSituation(val: string | null) {
    this.financialSituation = val;
  }

  @action
  setBuildTimeline(val: string | null) {
    this.buildTimeline = val;
  }

  @action
  setLeadStatus(val: string | null) {
    this.leadStatus = val;
  }

  @action
  setLandSituation(val: string | null) {
    this.landSituation = val;
  }

  @action
  setLandRegistrationDate(val: string) {
    this.landRegistrationDate = val;
  }

  @action
  setFinalComments(val: string) {
    this.finalComments = val;
  }

  @action
  setConsultant(consultant: Consultant | null) {
    this.consultant = consultant;
  }

  @action setRememberDetails(rememberDetails: boolean) {
    this.rememberDetails = rememberDetails;
  }

  @action restoreSessionState(
    homeOrHouseLand: Home | HouseLand,
    sessionState: SessionState
  ) {
    this.enteredViaRestoreSession = true;

    // The opposite of serialize - restore a session from the website database
    this.firstName = sessionState.first_name;
    this.lastName = sessionState.last_name;
    this.phone = sessionState.phone;
    this.email = sessionState.email;
    this.emailSecondary = sessionState.email_secondary;
    this.optIn = sessionState.opt_in;
    this.landType = sessionState.land_type;

    this.apiStore.setRegion(sessionState.region);
    this.apiStore.setEstate(sessionState.estate);
    this.apiStore.setEstateOther(sessionState.estate_other);
    this.apiStore.setLotWidth(sessionState.lot_width);
    this.setLandSituation(sessionState.land_situation);
    this.setLandRegistrationDate(sessionState.land_registration_date ? sessionState.land_registration_date : "");

    if (sessionState.home_id) {
      this.setSelectedHome(homeOrHouseLand);
    } else {
      this.setSelectedHouseLand(homeOrHouseLand as HouseLand);
    }
    this.setIsFloorplanFlipped(
      homeOrHouseLand.id,
      sessionState.is_floorplan_flipped
    );
    const floorplanOptions = homeOrHouseLand.floorplan_options.filter(
      (item) => {
        return sessionState.floorplan_ids.indexOf(item.id) > -1;
      }
    );
    this.setSelectedFloorplanOptions(homeOrHouseLand.id, floorplanOptions);

    if (sessionState.facade_id) {
      const facades = homeOrHouseLand.facades.filter(
        (item) => item.id === sessionState.facade_id
      );
      if (facades.length) {
        this.setSelectedFacade(facades[0]);

        const facadeFinishes = facades[0].finish_options.filter(
          (item) => item.id === sessionState.facade_finish_id
        );

        if (facadeFinishes.length) {
          this.setSelectedFacadeFinishOption(facades[0].id, facadeFinishes[0]);
        }
      }
    }

    if (sessionState.exterior_style_id) {
      const exteriors = this.apiStore.exteriorStyleResults.filter(
        (item) => item.id === sessionState.exterior_style_id
      );
      if (exteriors.length) {
        this.setSelectedExteriorStyle(exteriors[0]);

        const roofs = exteriors[0].roof_options.filter(
          (item) => item.id === sessionState.roof_style_id
        );
        if (roofs.length) {
          this.setSelectedRoofStyle(exteriors[0].id, roofs[0]);
        }

        const windows = exteriors[0].window_options.filter(
          (item) => item.id === sessionState.window_style_id
        );
        if (windows.length) {
          this.setSelectedWindowStyle(exteriors[0].id, windows[0]);
        }

        const gutters = exteriors[0].gutter_options.filter(
          (item) => item.id === sessionState.gutter_style_id
        );
        if (windows.length) {
          this.setSelectedGutterStyle(exteriors[0].id, gutters[0]);
        }
      }
    }

    if (sessionState.interior_style_id) {
      const interiors = this.apiStore.interiorStyleResults.filter(
        (item) => item.id === sessionState.interior_style_id
      );
      if (interiors.length) {
        this.setSelectedInteriorStyle(interiors[0]);
      }

      const kitchens = interiors[0].kitchen_options.filter(
        (item) => item.id === sessionState.kitchen_style_id
      );
      if (kitchens.length) {
        this.setSelectedKitchenStyle(interiors[0].id, kitchens[0]);
      }
      const flooring = interiors[0].flooring_options.filter(
        (item) => item.id === sessionState.flooring_style_id
      );
      if (flooring.length) {
        this.setSelectedFlooringStyle(interiors[0].id, flooring[0]);
      }
    }

    if (sessionState.promotion_id) {
      const promotions = homeOrHouseLand.promotions.filter(
        (item) => item.id === sessionState.promotion_id
      );

      if (promotions.length) {
        this.setSelectedPromotion(promotions[0]);
      }
    }

    if (sessionState.special_offer_id) {
      const promotions = homeOrHouseLand.promotions.filter(
        (item) => item.id === sessionState.special_offer_id
      );

      if (promotions.length) {
        this.setSelectedSpecialOffer(promotions[0]);
      }
    }

    if (sessionState.inclusion_id) {
      const inclusions = this.inclusionsWithPromoPricing.filter(
        (item) => item.id === sessionState.inclusion_id
      );

      if (inclusions.length) {
        this.setSelectedInclusion(inclusions[0]);
      }
    }

    if (sessionState.turn_inclusion_id) {
      const inclusions = this.turnKeyInclusionsWithPromoPricing.filter(
        (item) => item.id === sessionState.turn_inclusion_id
      );

      if (inclusions.length) {
        this.setSelectedTurnKeyInclusion(inclusions[0]);
      }
    }

    const upgradeCatIds = Object.keys(sessionState.upgrade_ids);
    for (const catId of upgradeCatIds) {
      const upgradeId = sessionState.upgrade_ids[parseInt(catId)];
      const upgrades = this.upgradesWithPromoPricing.filter(
        (item) => item.id === upgradeId
      );
      if (upgrades.length) {
        this.setSelectedUpgrade(parseInt(catId), upgrades[0]);
      }
    }

    const lifestyleCatIds = Object.keys(sessionState.lifestyle_option_ids);
    for (const catId of lifestyleCatIds) {
      const lifestyleIds = sessionState.lifestyle_option_ids[parseInt(catId)];
      const lifestyleOptions = this.lifestyleOptionsWithPromoPricing.filter(
        (item) => {
          return lifestyleIds.indexOf(item.id) > -1;
        }
      );
      this.selectedLifestyleOptions.set(parseInt(catId), lifestyleOptions);
    }

    this.applyForGrant = sessionState.apply_for_grant;
    if (sessionState.perc_complete === 100) {
      this.apiStore.grantSettings = sessionState.grant_settings;
      this.apiStore.finalDetailsSettings = sessionState.final_details_settings;
    }
  }

  serialize(): string {
    let homeId = null;
    let houseLandId = null;
    let targetId = null;
    let isFloorplanFlipped = false;
    let floorplanIds: number[] = [];

    if (
      this.landType === LandType.HOUSE_LAND_PACKAGE &&
      this.selectedHouseLand
    ) {
      houseLandId = this.selectedHouseLand.id;
      targetId = houseLandId;
    } else if (
      this.landType !== LandType.HOUSE_LAND_PACKAGE &&
      this.selectedHome
    ) {
      homeId = this.selectedHome.id;
      targetId = homeId;
    }

    if (targetId) {
      const floorplanOptions = this.selectedFloorplanOptions.get(targetId);
      if (floorplanOptions) {
        floorplanIds = floorplanOptions.map((item) => item.id);
      }

      isFloorplanFlipped = !!this.isFloorplanFlipped.get(targetId);
    }

    let facadeId = null;
    let facadeRendivoId = null;
    let facadeFinishId = null;
    let facadeFinishRendivoId = null;
    if (this.selectedFacade) {
      facadeId = this.selectedFacade.id;
      facadeRendivoId = this.selectedFacade.rendivo_id;
      const facadeFinish = this.selectedFacadeFinishOption.get(facadeId);
      if (facadeFinish) {
        facadeFinishId = facadeFinish.id;
        facadeFinishRendivoId = facadeFinish.rendivo_id;
      }
    }

    if (
      this.landType === LandType.HOUSE_LAND_PACKAGE &&
      this.selectedHouseLand !== null
    ) {
      facadeRendivoId = this.selectedHouseLand.rendivo_id;
      facadeFinishRendivoId = this.selectedHouseLand.rendivo_theme_id;
    }

    let exteriorStyleId = null;
    let roofStyleId = null;
    let windowStyleId = null;
    let gutterStyleId = null;
    let exteriorRendivoStyleId = null;
    let roofStyleRendivoId = null;
    let windowStyleRendivoId = null;
    let gutterStyleRendivoId = null;
    if (this.selectedExteriorStyle) {
      exteriorStyleId = this.selectedExteriorStyle.id;
      exteriorRendivoStyleId = this.selectedExteriorStyle.rendivo_id;
      const roofStyle = this.selectedRoofStyle.get(exteriorStyleId);
      if (roofStyle) {
        roofStyleId = roofStyle.id;
        roofStyleRendivoId = roofStyle.rendivo_id;
      }

      const windowStyle = this.selectedWindowStyle.get(exteriorStyleId);
      if (windowStyle) {
        windowStyleId = windowStyle.id;
        windowStyleRendivoId = windowStyle.rendivo_id;
      }

      const gutterStyle = this.selectedGutterStyle.get(exteriorStyleId);
      if (gutterStyle) {
        gutterStyleId = gutterStyle.id;
        gutterStyleRendivoId = gutterStyle.rendivo_id;
      }
    }

    const inclusionId = this.selectedInclusion
      ? this.selectedInclusion.id
      : null;

    const turnKeyInclusionId = this.selectedTurnKeyInclusion
      ? this.selectedTurnKeyInclusion.id
      : null;

    const promotionId = this.selectedPromotion
      ? this.selectedPromotion.id
      : null;
    const specialOfferId = this.selectedSpecialOffer
      ? this.selectedSpecialOffer.id
      : null;

    const upgradeIds: Record<number, number> = {};
    for (const entry of Array.from(this.selectedUpgrades)) {
      // Key = Upgrade Category
      // Value = Upgrade ID
      upgradeIds[entry[0]] = entry[1].id;
    }

    const lifestyleOptionIds: Record<number, number[]> = {};
    for (const entry of Array.from(this.selectedLifestyleOptions)) {
      // Key = Lifestyle Category
      // Value = Lifestyle ID
      lifestyleOptionIds[entry[0]] = entry[1].map((item) => item.id);
    }

    let interiorStyleId = null;
    let kitchenStyleId = null;
    let flooringStyleId = null;
    if (this.selectedInteriorStyle) {
      interiorStyleId = this.selectedInteriorStyle.id;
      const kitchenStyle = this.selectedKitchenStyle.get(interiorStyleId);
      if (kitchenStyle) {
        kitchenStyleId = kitchenStyle.id;
      }
      const flooringStyle = this.selectedFlooringStyle.get(interiorStyleId);
      if (flooringStyle) {
        flooringStyleId = flooringStyle.id;
      }
    }

    const rendivoStyle = this.isCladdedSelected ? "CLADDED" : null;

    const state: SessionState = {
      region: this.apiStore.region,
      estate: this.apiStore.estate,
      estate_other: this.apiStore.estateOther,
      lot_width: this.apiStore.lotWidth,
      house_type: this.apiStore.houseType,
      build_situation: this.apiStore.buildSituation,
      current_route: this.currentRoute,
      current_step: this.currentStep,
      title: getPageTitle(this.currentRoute),
      perc_complete: this.percentComplete,
      total_price: this.totalPriceWithSiteWorks,
      first_name: this.firstName,
      last_name: this.lastName,
      phone: this.phone,
      email: this.email,
      email_secondary: this.emailSecondary,
      opt_in: this.optIn,
      apply_for_grant: this.applyForGrant,
      grant_settings: this.apiStore.grantSettings,
      financial_situation: this.financialSituation,
      build_timeline: this.buildTimeline,
      land_situation: this.landSituation,
      lead_status: this.leadStatus,
      land_registration_date: this.landRegistrationDate,
      final_comments: this.finalComments,
      consultant_id: this.consultant ? this.consultant.id : null,
      remember_details: this.rememberDetails,
      land_type: this.landType,
      home_id: homeId,
      house_land_id: houseLandId,
      is_floorplan_flipped: isFloorplanFlipped,
      floorplan_ids: floorplanIds,
      facade_id: facadeId,
      facade_finish_id: facadeFinishId,
      exterior_style_id: exteriorStyleId,
      roof_style_id: roofStyleId,
      window_style_id: windowStyleId,
      gutter_style_id: gutterStyleId,
      rendivo_style: rendivoStyle,
      facade_rendivo_id: facadeRendivoId,
      facade_finish_rendivo_id: facadeFinishRendivoId,
      exterior_style_rendivo_id: exteriorRendivoStyleId,
      roof_style_rendivo_id: roofStyleRendivoId,
      window_style_rendivo_id: windowStyleRendivoId,
      gutter_style_rendivo_id: gutterStyleRendivoId,
      inclusion_id: inclusionId,
      turn_inclusion_id: turnKeyInclusionId,
      upgrade_ids: upgradeIds,
      lifestyle_option_ids: lifestyleOptionIds,
      interior_style_id: interiorStyleId,
      flooring_style_id: flooringStyleId,
      kitchen_style_id: kitchenStyleId,
      promotion_id: promotionId,
      special_offer_id: specialOfferId,
      send_to_finance_manager: this.sendToFinanceManager,
      interested_in_granny_flats: this.interestedInGrannyFlats,
      consent: this.consent,
      final_details_settings: this.apiStore.finalDetailsSettings,
      price_disclaimers: this.apiStore.finalDetailsPriceDisclaimers,
      finance_cta_button: this.apiStore.finalDetailsFinanceCtaButton,
      summary_popup_button: this.apiStore.finalDetailsSummaryPopupButton,
    };
    return JSON.stringify(state);
  }

  getRouteList(): RoutePath[] {
    let routeList = [];
    if (this.landType === LandType.HOUSE_LAND_PACKAGE) {
      if(this.isKioskMode){
        routeList = NSW_HOUSE_LAND_KIOSK_ROUTES;
      }else{
        routeList = NSW_HOUSE_LAND_ROUTES;
      }
    } else {
      if(this.isKioskMode){
        routeList = NSW_HOME_KIOSK_ROUTES;
      }else{
        routeList = NSW_HOME_ROUTES;
      }
    }
    return routeList;
  }

  getPriceOrPromoPrice(item: Inclusion | Upgrade) {
    if (item.promo_price !== undefined) {
      return item.promo_price;
    }
    return item.price;
  }

  debugTotals() {
    if (IS_DEV) {
      const table = {
        base_price: this.basePrice,
        floorplans: this.floorplanPrice,
        facades: this.facadePrice,
        exterior: this.exteriorStylePrice,
        inclusions: this.inclusionsPrice,
        upgrades: this.upgradesPrice,
        turn_key: this.turnKeyPrice,
        interior: this.interiorStylePrice,
        lifestyle: this.lifestyleOptionsPrice,
        siteworks: this.siteWorksAmount,
        z_subtotal: this.subtotalPrice,
        zz_total: this.totalPrice,
      };
      console.log(table);
    }
  }
}
